import { cached, tracked } from '@glimmer/tracking';

import type { DataLabel, DataLabelPart, DSData } from 'webapp-types';
import type { Aggregation } from 'webapp/enums/enums';
import {
	BlockVisualization,
	DataLabelType,
	OrderDirection,
	SortFieldType,
	TableColumnType,
} from 'webapp/enums/enums';
import {
	buildDataLabelPartsFromDimensionsAndValuesMap,
	compact,
	glueDimensionValues,
	uniq,
} from 'webapp/utils/common';
import {
	formatNumber,
	getAggregationLabel,
	hashCode,
	useAggregationOnValues,
} from 'webapp/utils/number';

import type { MetricSetup } from 'webapp/models-custom';
import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type DsDataOutputOtherLevel from 'webapp/models-custom/ds-data/output/other';
import DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

const deltaSign = 'Δ';

interface TableStructureColumnHeader {
	index: number; // zero-base}
	label: string;
	compareTitle: string; // if this column has compare, the column is split in two. only show titles from compare when the same for whole column, othrwise just delta sign. empty if no compares
	ts?: number; // only for TableColumnType.GRANULATIONS
	dsId?: number; // only for TableColumnType.METRICS, used for cell click propagation (AppEvent.BLOCK_CONTENT_CLICKED), and also matching columns when sorting
}

export interface TableStructureCell {
	isAggregation: false;
	headerIndex: number; // zero-based
	points: DChartPrimaryPoint[];
	hasCompare?: boolean; // if this column has compare, the column is split in two
	showCompareTooltip?: boolean; // if this column has different compares, we show the compare in tooltip
	dsId?: number; // used for cell click propagation (AppEvent.BLOCK_CONTENT_CLICKED), and possibly getting aggregation setting for aggregation row / column,
}

export interface TableStructureRow {
	label: DataLabel;
	cells: TableStructureCell[];
	unsortedIndex: number; // 0-based
	sortedIndex?: number; // 1-based, used in the view. set after sorting
	isOther?: boolean;
	dsId?: number; // only for TableColumnType.SINGLE or TableColumnType.GRANULATIONS, used only for cell click propagation (AppEvent.BLOCK_CONTENT_CLICKED)
	mediaUrl?: string;
}

interface TableStructure {
	columnHeaders: TableStructureColumnHeader[];
	rows: TableStructureRow[];
}

interface TableStructureGroup {
	// group of cells - either from row or column - used when setting aggregations
	cells: TableStructureCell[];
	isOther?: boolean;
}

interface AggregationPoint {
	unit: string;
	point: DChartPrimaryPoint;
	values: number[];
	valueStr: string; // the final value, including also outputsOther from the cell
}

export interface AggregationCell {
	isAggregation: true;
	type: Aggregation | null; // null means cell is empty
	points: AggregationPoint[];
	outputsOther?: DsDataOutputOtherLevel[];
	label?: string; // only if aggregations are different
	hasCompare?: boolean; // if this column has compare, the column is split in two
	showCompareTooltip?: false; // just for ts reasons
	dsId?: undefined; // just for ts reasons
}

interface AggregationData {
	label: string;
	cells: AggregationCell[];
}

type AutoResolution =
	{ mode: 'single' } | { mode: 'metrics' } | { mode: 'two-dim'; rowDim: string; colDim: string };

const RecordLimit = 100;

export default class VisualizationTable extends VisualizationBase {
	visualizationType = BlockVisualization.TABLE;

	recordLimit = RecordLimit;

	get tableColumnType(): TableColumnType {
		return this.block.tableColumnType ?? TableColumnType.AUTO;
	}

	get isColumnsMetrics() {
		return (
			this.tableColumnType === TableColumnType.METRICS ||
			(this.tableColumnType === TableColumnType.AUTO &&
				this.autoResolution.mode === 'metrics')
		);
	}

	get isColumnsGranulations() {
		return this.tableColumnType === TableColumnType.GRANULATIONS;
	}

	get isColumnsSingle() {
		return (
			this.tableColumnType === TableColumnType.SINGLE ||
			(this.tableColumnType === TableColumnType.AUTO && this.autoResolution.mode === 'single')
		);
	}

	get isTwoDimLayout() {
		return (
			this.tableColumnType === TableColumnType.AUTO && this.autoResolution.mode === 'two-dim'
		);
	}

	get labelColumnCount(): number {
		const max = this.tableStructure.rows.reduce(
			(acc, row) => Math.max(acc, row.label.parts.length),
			0,
		);
		return Math.max(1, max);
	}

	@cached
	private get autoResolution(): AutoResolution {
		// Decides how AUTO column type renders, based on the dimensions actually present in
		// the data (Object.keys of each output's dimensionValues). The dimensioned (two-dim)
		// layout is reserved for a single metric with exactly two dimensions; anything more
		// dimensioned falls back to the flat single layout.
		//   - 0 DSes → single.
		//   - 1 DS (single-metric):
		//       · exactly 2 dims → two-dim. Row vs column follows the data-select's configured
		//         dimension order (first selected dim = rows, second = columns), not payload order.
		//       · 0, 1, or >2 dims → single.
		//   - Multi-DS (multi-metric):
		//       · max dim count = 1 → metrics (METRICS layout). Covers all-share-same-dim,
		//         each-has-different-single-dim, and mixed 1-dim / 0-dim shapes.
		//       · max dim count = 0 or >1 → single.

		const dsList = this.blockSetup.sortedDataSelectsShown;
		const dimsPerDs = dsList.map((ds) => {
			const dims: string[] = [];
			(ds.dataRoot?.outputs || []).forEach((output) => {
				Object.keys(output.dimensionValues).forEach((key) => {
					if (!dims.includes(key)) {
						dims.push(key);
					}
				});
			});
			return { dsId: ds.numericId || 0, dims };
		});

		if (dimsPerDs.length === 0) {
			return { mode: 'single' };
		}
		if (dimsPerDs.length === 1) {
			const only = dimsPerDs[0]!;
			if (only.dims.length === 2) {
				// Order rows/columns by the data-select's configured dimension order (first
				// selected dim = rows, second = columns), regardless of the payload key order.
				// Keys missing from setup keep their data order (sorted last).
				const setupDims = dsList[0]!.metricSetup.dimensions || [];
				const orderIndex = (key: string) => {
					const i = setupDims.indexOf(key);
					return i === -1 ? Number.MAX_SAFE_INTEGER : i;
				};
				const [rowDim, colDim] = [...only.dims].sort(
					(a, b) => orderIndex(a) - orderIndex(b),
				);
				return { mode: 'two-dim', rowDim: rowDim!, colDim: colDim! };
			}
			return { mode: 'single' };
		}

		// multiple DSes (multi-metric): only the single-dim METRICS layout is supported
		const maxDimsCount = Math.max(...dimsPerDs.map(({ dims }) => dims.length));
		if (maxDimsCount === 1) {
			return { mode: 'metrics' };
		}
		return { mode: 'single' };
	}

	get selectedSortDataSelectId(): number | null {
		const firstDataSelect = this.blockSetup.firstShownDataSelect;
		return firstDataSelect?.sortDataSelect || firstDataSelect?.numericId || null;
	}

	// -------- visualization settings ---------

	override get hasGranulation(): boolean {
		return this.isColumnsGranulations;
	}

	override get showAggregationChoiceOnBlock(): boolean {
		// in case of single column, there is only one aggregation
		// that is shown on the bottom
		return this.isColumnsSingle;
	}

	override get showAggregationChoiceOnDs(): boolean {
		return !this.showAggregationChoiceOnBlock;
	}

	override get enabledSortOptions(): LabelValuePairString[] {
		let options = Array.from(super.enabledSortOptions);
		if (this.isColumnsGranulations) {
			// remove sort by change option
			options = options.filter((o) => o.value !== SortFieldType.CHANGE);
		}
		return options;
	}

	override get showMediaSwitch(): boolean {
		return this.blockSetup.dataSelects.some((dataSelect) => {
			return dataSelect.metricSetup.metric?.hasMedia;
		});
	}

	override get showBlockRecordLimitChoice() {
		return this.isColumnsMetrics;
	}

	override get allowAutoUpdateOnPrimary() {
		return !this.isColumnsGranulations;
	}

	override get showSortDataSelect() {
		return this.isColumnsMetrics;
	}

	// ------- data for display --------

	get allowOthersRow() {
		return !!this.block.showOther;
	}

	get showAggregation() {
		const dsShown = this.blockSetup.dataSelectsShown;
		return dsShown.some((ds) => !!ds.aggregation);
	}

	get showAggregationAsRow() {
		const dsShown = this.blockSetup.dataSelectsShown;
		return (
			this.showAggregation &&
			(this.isColumnsMetrics || dsShown.length <= 1 || this.showAggregationChoiceOnBlock)
		);
	}

	get showAggregationAsColumn() {
		return this.showAggregation && !this.showAggregationAsRow;
	}

	get defaultLabelHeader() {
		if (this.isColumnsMetrics) {
			return 'Dimension';
		} else {
			return 'Metric';
		}
	}

	get labelHeader() {
		return this.block.labelHeader || this.defaultLabelHeader;
	}

	override get showLabelHeader() {
		return super.showLabelHeader && this.tableColumnType !== TableColumnType.AUTO;
	}

	get labelColumnHeaders(): string[] {
		const count = this.labelColumnCount;

		if (this.tableColumnType !== TableColumnType.AUTO) {
			return Array.from({ length: count }, (_, i) => (i === 0 ? this.labelHeader : ''));
		}

		const getDimensionLabel = (key: string) => {
			for (const ds of this.blockSetup.sortedDataSelectsShown) {
				const label = ds.dataRoot?.getDimensionLabel(key);
				if (label) {
					return label;
				}
			}
			return undefined;
		};

		const resolution = this.autoResolution;
		if (resolution.mode === 'two-dim') {
			const rowLabel = getDimensionLabel(resolution.rowDim) ?? '';
			const colLabel = getDimensionLabel(resolution.colDim) ?? '';
			return [`${rowLabel} / ${colLabel}`];
		}

		// single / metrics: a column shows a dimension label only when every dimension part
		// at that index shares one key; mixed-dimension columns (and metric columns) are blank.
		const rows = this.tableStructure.rows;
		return Array.from({ length: count }, (_, i) => {
			const keys = new Set<string>();
			let label = '';
			rows.forEach((row) => {
				const part = row.label.parts[i];
				if (part?.type === DataLabelType.DIMENSION && part.dimension) {
					keys.add(part.dimension.key);
					label = part.dimension.value;
				}
			});
			return keys.size === 1 ? label : '';
		});
	}

	@tracked tableStructure: TableStructure = { columnHeaders: [], rows: [] };

	@tracked aggregationData: AggregationData = { label: '', cells: [] };

	override updateDataCache() {
		this.tableStructure = this.getTableStructure();

		// aggregationData will calculate on already sorted tableStructure
		this.aggregationData = this.getAggregationData();
	}

	override clearDataCache() {
		this.tableStructure = { columnHeaders: [], rows: [] };
		this.aggregationData = { label: '', cells: [] };
	}

	private getTableStructure() {
		const structure = this.getTableStructureUnsorted();

		const sortField = this.block.sortField || SortFieldType.VALUE,
			sortOrder = this.block.sortOrder || OrderDirection.DESC;

		let sortCellIndex: number | null = null;
		if (this.isColumnsMetrics) {
			// get column index for the DS that we sort on
			const sortDataSelectId = this.blockSetup.dataSelects.find((ds) => {
				return !!ds.sortDataSelect;
			})?.sortDataSelect;
			if (sortDataSelectId) {
				const header = structure.columnHeaders.find((h) => h.dsId === sortDataSelectId);
				if (header) {
					sortCellIndex = header.index;
				}
			}
		}

		structure.rows.sort((a, b) => {
			return this.doRowCompare(a, b, sortField, sortOrder, sortCellIndex);
		});

		structure.rows.forEach((row, idx) => {
			row.sortedIndex = idx + 1; // using in the view to number rows
		});

		return structure;
	}

	@cached
	private get tableOutputsByMetricAttributeCombo(): DsDataOutputTopLevel[][] {
		// top level = all metric + attribute combos
		// second level = different units for this metric + attribute combo
		// (we show all units in the same table cell)
		// this should be filtered / transformed forward depending on table layout
		const outputGroups: DsDataOutputTopLevel[][] = [];
		this.tableOutputsByMetric.forEach((outputsFromMetric) => {
			outputGroups.push(...outputsFromMetric);
		});
		return outputGroups;
	}

	@cached
	private get tableOutputsByMetric(): DsDataOutputTopLevel[][][] {
		// top level = metrics (=ds)
		// second level = by attribute
		// third level = different units for this metric + attribute combo
		// (we show all units in the same table cell)
		// this should be filtered / transformed forward depending on table layout
		const outputGroups: DsDataOutputTopLevel[][][] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const outputGroupsByDimensionValues: {
				dimensionValues: DSData.DimensionsAndValuesMap;
				outputs: DsDataOutputTopLevel[];
			}[] = [];
			(ds.dataRoot?.outputs || []).forEach((output) => {
				const existingGroup = outputGroupsByDimensionValues.find((item) =>
					output.equalDimensionValues(item.dimensionValues),
				);
				if (existingGroup) {
					existingGroup.outputs.push(output);
				} else {
					outputGroupsByDimensionValues.push({
						dimensionValues: output.dimensionValues,
						outputs: [output],
					});
				}
			});
			const outputs = outputGroupsByDimensionValues.map(
				(item: { outputs: DsDataOutputTopLevel[] }) => item.outputs,
			);
			if (outputs.length) {
				outputGroups.push(outputs);
			} else if (ds.dataRoot) {
				// ds with no data -> create "fake" output with no data
				// so we can still show this metric in the list (table-specific requirement)
				const output = new DsDataOutputTopLevel(ds.dataRoot, { items: [] });
				outputGroups.push([[output]]);
			}
		});
		return outputGroups;
	}

	@cached
	private get tableOutputsByAttribute(): DsDataOutputTopLevel[][][] {
		// top level = by attribute
		// second level = metrics (=ds)
		// third level = different units for this metric + attribute combo
		// (we show all units in the same table cell)
		// this should be filtered / transformed forward depending on table layout
		const outputGroups: DsDataOutputTopLevel[][][] = [];

		const outputGroupsByDimensionValues: {
			dimensionValues: DSData.DimensionsAndValuesMap;
			outputs: { key: string; outputs: DsDataOutputTopLevel[] }[];
		}[] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const key = this.getUniqueMetricSetupDataString(ds.metricSetup);
			(ds.dataRoot?.outputs || []).forEach((output) => {
				// cross-metric join: match by dimension value only, since each metric may
				// label the same value under a different dimension key
				const existingAttributeGroup = outputGroupsByDimensionValues.find((item) =>
					output.equalAttributeNames(item.dimensionValues),
				);
				if (existingAttributeGroup) {
					const existingMetricGroup = existingAttributeGroup.outputs.find(
						(item) => item.key === key,
					);
					if (existingMetricGroup) {
						existingMetricGroup.outputs.push(output);
					} else {
						existingAttributeGroup.outputs.push({ key, outputs: [output] });
					}
				} else {
					outputGroupsByDimensionValues.push({
						dimensionValues: output.dimensionValues,
						outputs: [{ key, outputs: [output] }],
					});
				}
			});
		});

		outputGroupsByDimensionValues.forEach((groupByAttribute) => {
			const group = groupByAttribute.outputs.map((grp) => grp.outputs);
			outputGroups.push(group);
		});

		return outputGroups;
	}

	private getTableStructureUnsorted(): TableStructure {
		let tableStructure: TableStructure;
		if (this.isColumnsMetrics) {
			tableStructure = this.getTableStructureForColumnsMetrics();
		} else if (this.isColumnsGranulations) {
			tableStructure = this.getTableStructureForColumnsGranulations();
		} else if (this.isTwoDimLayout) {
			const { rowDim, colDim } = this.autoResolution as Extract<
				AutoResolution,
				{ mode: 'two-dim' }
			>;
			tableStructure = this.getTableStructureForColumnsAuto(rowDim, colDim);
		} else {
			tableStructure = this.getTableStructureForColumnsSingle();
		}

		// fill the rows with empty cells til the end, if needed
		// add hasCompare to cells depending on value in the headers
		const columns = tableStructure.columnHeaders;
		tableStructure.rows.forEach((row) => {
			for (let i = 0; i < columns.length; i++) {
				const column = columns[i]!;
				let existingCell = row.cells[i];
				if (!existingCell) {
					row.cells[i] = existingCell = {
						headerIndex: i,
						points: [],
						dsId: column.dsId || undefined,
						isAggregation: false,
					};
				}
				existingCell.hasCompare = !!column.compareTitle;
				existingCell.showCompareTooltip = column.compareTitle === deltaSign;
			}
		});
		return tableStructure;
	}

	private getAggregationData(): AggregationData {
		let tableStructureGroups: TableStructureGroup[] = [],
			cellCount = 0;
		if (this.showAggregationAsRow) {
			// using rows as-is
			tableStructureGroups = this.tableStructure.rows;
			cellCount = this.tableStructure.columnHeaders.length;
		} else if (this.showAggregationAsColumn) {
			// we need to use columns, so rotate the table
			this.tableStructure.rows.forEach((row, rowIdx) => {
				row.cells.forEach((cell, cellIdx) => {
					if (!tableStructureGroups[cellIdx]) {
						tableStructureGroups[cellIdx] = { cells: [] };
					}
					tableStructureGroups[cellIdx].cells[rowIdx] = cell;
				});
			});
			cellCount = this.tableStructure.rows.length;
		}
		const aggregationData = this.getAggregationDataForTableStructureGroups(
			tableStructureGroups,
			cellCount,
		);

		if (this.showAggregationAsRow) {
			// add hasCompare to cells depending on value in the headers
			const columns = this.tableStructure.columnHeaders;
			for (let i = 0; i < columns.length; i++) {
				const column = columns[i]!;
				aggregationData.cells[i]!.hasCompare = !!column.compareTitle;
			}
		}

		return aggregationData;
	}

	private getAggregationDataForTableStructureGroups(
		tableStructureGroups: TableStructureGroup[],
		cellCount: number,
	): AggregationData {
		const aggregationsByCell: (Aggregation | undefined)[] = [];

		const pointsAndOthersForAllCells: {
			points: AggregationPoint[];
			outputsOther: DsDataOutputOtherLevel[];
		}[] = [];

		// group all points and other-s (aggregation cell cell) together
		tableStructureGroups.forEach((tableStructure) => {
			tableStructure.cells.forEach((cell, idx) => {
				const cellPoints = cell?.points || [],
					ds = cell.dsId
						? this.blockSetup.dataSelects.find((item) => item.id === String(cell.dsId))
						: null;

				if (ds?.aggregation) {
					aggregationsByCell[idx] = ds.aggregation;
				}

				// prepare cell data, if not yet done
				if (!pointsAndOthersForAllCells[idx]) {
					pointsAndOthersForAllCells[idx] = {
						points: [],
						outputsOther: [],
					};
				}

				const pointsAndOthersForCell = pointsAndOthersForAllCells[idx];

				if (tableStructure.isOther) {
					if (ds) {
						const firstPoint = cellPoints[0],
							dimensionValues =
								firstPoint?.metricSelectionData?.dimensionValues || {},
							unit = firstPoint?.metricSelectionData?.unit || '',
							ts = firstPoint?.timestamp;
						const outputOther =
							ds.dataRoot?.getOtherOutputByDimensionValuesUnitAndTimestamp(
								dimensionValues,
								unit,
								ts,
							);
						if (outputOther) {
							pointsAndOthersForCell.outputsOther.push(outputOther);
						}
					}
				} else {
					cellPoints.forEach((point) => {
						const unit = point.metricSelectionData?.unit || '',
							value = point.value!, // only existing points so far, we fill empties later
							existingPoint = pointsAndOthersForCell.points.find(
								(item) => item.unit === unit,
							);
						if (existingPoint) {
							existingPoint.values.push(value);
						} else {
							pointsAndOthersForCell.points.push({
								unit,
								point,
								values: [value],
								valueStr: '', // setting later for the aggregation
							});
						}
					});
				}
			});
		});

		// find if there is more than one aggregation for this table (influences text labels)
		const uniqueAggregations = uniq(compact(aggregationsByCell)),
			commonAggregation = uniqueAggregations.length === 1 ? uniqueAggregations[0]! : null;

		// prepare end results (valueStr-s and optional labels) for each cell
		const cells: AggregationCell[] = [];
		pointsAndOthersForAllCells.forEach((pointData, idx) => {
			const cellAggregation = aggregationsByCell[idx];
			if (cellAggregation) {
				pointData.points.forEach((point) => {
					const otherOutput = pointData.outputsOther.find(
							(item) => item.unit === point.unit,
						),
						value = useAggregationOnValues(
							cellAggregation,
							point.values,
							otherOutput?.points || [],
							otherOutput?.count || 0,
						);
					if (typeof value === 'number') {
						point.valueStr = formatNumber(value, point.point.format, {
							scale: point.point.scale,
						});
					}
				});
				cells[idx] = {
					isAggregation: true,
					type: cellAggregation,
					label: commonAggregation ? '' : getAggregationLabel(cellAggregation),
					points: pointData.points,
					dsId: undefined,
				};
			}
		});

		// fill the cells til the end, if needed
		for (let i = 0; i < cellCount; i++) {
			let existingCell = cells[i];
			if (!existingCell) {
				cells[i] = existingCell = {
					isAggregation: true,
					type: null,
					points: [],
					dsId: undefined,
				};
			}
		}

		return {
			label: commonAggregation ? getAggregationLabel(commonAggregation) : '',
			cells,
		};
	}

	private doRowCompare(
		row1: TableStructureRow,
		row2: TableStructureRow,
		sortField: SortFieldType | null,
		sortOrder: OrderDirection,
		byCellIndex: number | null,
	) {
		// sort by name and custom - trivial
		// sort by value / change:
		// - if byCellIndex is given, only the values in the given cell are evaluated (column:metrics scenario)
		// - if byCellIndex is not given, and there are multiple cells (column:granulations scenario), we sum them
		// - if we sort by change, only first change in the cell is taken into account.
		// - if sorting by change/value, the null values (no value / no change) must always be on bottom independently of sortOrder
		// - columns:granulations cannot be sorted by change
		// note that the aggregation row is added later (in row or column format) and sorted separately
		let result = 0,
			mustApplyReverse = sortOrder === OrderDirection.DESC;
		switch (sortField) {
			case SortFieldType.CUSTOM:
				return 0; // dont apply sortorder
			case SortFieldType.NAME:
				result = row1.label.text.localeCompare(row2.label.text);
				break;
			case SortFieldType.CHANGE: {
				// we cannot sum changes, so we always need one cell picked
				// thats why we fallback to 0 (first column)
				const targetCell1 = row1.cells[byCellIndex || 0],
					targetCell2 = row2.cells[byCellIndex || 0],
					point1 = targetCell1?.points.find((p) => p.hasCompare),
					point2 = targetCell2?.points.find((p) => p.hasCompare);
				result = DChartPrimaryPoint.doPointCompare(point1, point2, sortField, sortOrder);
				mustApplyReverse = false; // bcos already done in doPointCompare
				break;
			}
			case SortFieldType.VALUE:
			default: {
				// gather all points that we will get value from
				// if there will be >1, we will need to do sum-ing
				const points1: DChartPrimaryPoint[] = [],
					points2: DChartPrimaryPoint[] = [];

				if (byCellIndex === null) {
					// add all points in all cells in these rows
					row1.cells.forEach((cell) => {
						points1.push(...cell.points);
					});
					row2.cells.forEach((cell) => {
						points2.push(...cell.points);
					});
				} else {
					const targetCell1 = row1.cells[byCellIndex]!,
						targetCell2 = row2.cells[byCellIndex]!;
					points1.push(...targetCell1.points);
					points2.push(...targetCell2.points);
				}

				let value1: number | null = null,
					value2: number | null = null;

				if (points1.length > 1) {
					points1.forEach((p) => {
						if (p.hasValue) {
							if (value1 === null) {
								value1 = 0;
							}
							value1 += p.value!;
						}
					});
				} else if (points1.length === 1) {
					value1 = points1[0]!.value;
				}

				if (points2.length > 1) {
					points2.forEach((p) => {
						if (p.hasValue) {
							if (value2 === null) {
								value2 = 0;
							}
							value2 += p.value!;
						}
					});
				} else if (points2.length === 1) {
					value2 = points2[0]!.value;
				}

				// avoid doPointCompare bcos we would need to create points for the value sum's (perf)
				result = DChartPrimaryPoint.doValueCompare(value1, value2);

				// if any of the values is null (missing), it must be always on bottom
				// doValueCompare already does so, which means we must skip applying DESC sort
				if (value1 === null || value2 === null) {
					mustApplyReverse = false;
				}
			}
		}

		if (mustApplyReverse) {
			// reverse the result
			if (result > 0) {
				result = -1;
			} else if (result < 0) {
				result = 1;
			}
		}

		return result;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				ct: this.tableColumnType,
				ci: block.tableColumnInvert,
				lh: this.labelHeader,
				sf: block.sortField,
				so: block.sortOrder,
				sho: block.showOther,
				a: dss.map((ds) => ds.aggregation),
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- data & visualization helpers -------

	private getUniqueMetricSetupDataString(metricSetup: MetricSetup): string {
		// not taking into account dimensionValues
		const metricSetupData = metricSetup.metricSetupData;
		if (!metricSetupData) {
			return '';
		}
		let key = `${metricSetupData.key}-${metricSetupData.spaceAccessId}`;
		if (metricSetupData.dimensions?.length) {
			key += `-${metricSetupData.dimensions.join('-')}`;
		}
		return key;
	}

	private getTableStructureForColumnsSingle(): TableStructure {
		const rows: TableStructureRow[] = [];

		let hasCompare = false,
			compareTitle = '';

		const tableOutputsByMetricAttributeCombo = this.tableOutputsByMetricAttributeCombo;
		const isMultiDs = this.blockSetup.sortedDataSelectsShown.length > 1;

		// just all metric and attribute combos
		tableOutputsByMetricAttributeCombo.forEach((outputsByMetricAndAttribute, idx) => {
			const points: DChartPrimaryPoint[] = [],
				firstOutput = outputsByMetricAndAttribute[0]!,
				ds = firstOutput.dataRoot.dataSelect,
				dsId = ds?.numericId || undefined;

			outputsByMetricAndAttribute.forEach((output) => {
				if (output.point) {
					const point = this.createDChartPointFromPoint(output, output.point);
					points.push(point);
					if (!hasCompare && point.comparePoints.length) {
						hasCompare = true;
					}
				}
			});

			// with multiple DSes the dim values alone (e.g. "USA / Organic") don't
			// say which metric the row belongs to. Prefix with the metric name in
			// that case. The override-set and no-attribute-names branches of
			// shortTitleWithoutUnit already produce a self-identifying label.
			let parts = firstOutput.dataLabelParts;
			let text = firstOutput.shortTitleWithoutUnit;
			if (
				isMultiDs &&
				firstOutput.hasAttributeNames &&
				!firstOutput.metricNameOverride &&
				!firstOutput.isOneSpecificAttributeOnSetup
			) {
				parts = [{ type: DataLabelType.METRIC, value: firstOutput.metricName }, ...parts];
				text = `${firstOutput.metricName} / ${text}`;
			}

			rows.push({
				unsortedIndex: idx,
				label: { parts, text }, // units will be grouped in cells
				mediaUrl: firstOutput.getDataRootMediaMapUrl(),
				dsId,
				cells: [
					{
						headerIndex: 0,
						points,
						dsId,
						isAggregation: false,
					},
				],
			});

			if (ds) {
				compareTitle = this.updateCompareColumnTitle(
					ds.limitations.compareCMs[0]!,
					compareTitle,
				);
			}
		});

		// fallback in case for some reason we had no ds to read compares from
		if (hasCompare && !compareTitle) {
			compareTitle = deltaSign;
		}

		// One "Other" row per metric (=ds)
		if (this.allowOthersRow) {
			const dataSelects = this.blockSetup.sortedDataSelectsShown,
				isMultipleOthers =
					dataSelects.filter((ds) => ds.dataRoot?.outputsOther.length).length > 1;
			dataSelects.forEach((ds) => {
				const dataRoot = ds.dataRoot,
					otherOutputs = dataRoot?.outputsOther || []; // multiple means multiple units
				if (dataRoot && otherOutputs.length) {
					const points: DChartPrimaryPoint[] = [];
					otherOutputs.forEach((output) => {
						const pointTotal = output.pointTotal;
						if (pointTotal) {
							const point = this.createDChartPointFromPoint(
								output,
								output.pointTotal,
							);
							points.push(point);
						}
					});

					const titlePrefix = isMultipleOthers ? `${dataRoot.metricName} / ` : '';
					const otherParts: DataLabelPart[] = isMultipleOthers
						? [
								{ type: DataLabelType.METRIC, value: dataRoot.metricName },
								{ type: DataLabelType.OTHER, value: 'Other' },
							]
						: [{ type: DataLabelType.OTHER, value: 'Other' }];
					const dsId = ds.numericId!;
					rows.push({
						unsortedIndex: rows.length,
						label: { parts: otherParts, text: `${titlePrefix}Other` },
						isOther: true,
						dsId,
						cells: [
							{
								headerIndex: 0,
								points,
								dsId,
								isAggregation: false,
							},
						],
					});
				}
			});
		}

		const columnHeaders = [
			{
				index: 0,
				label: 'Value',
				compareTitle,
			},
		];

		return {
			columnHeaders,
			rows,
		};
	}

	private getTableStructureForColumnsMetrics(): TableStructure {
		const columnHeaders: TableStructureColumnHeader[] = [],
			rows: TableStructureRow[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const dataRoot = ds.dataRoot;
			if (dataRoot) {
				const compare = ds.limitations.compareCMs[0],
					compareDataRoot = dataRoot.compareRoots[0];

				let compareTitle = '';
				if (compare && !compare.isNone) {
					if (compare.isOtherMetric && compareDataRoot?.metricName) {
						// compare.titleVs reads from the metrickey name, which we might not have here (e.g. public url)
						// so we read from the data here instead
						compareTitle = `vs ${compareDataRoot.metricName}`;
					} else {
						compareTitle = compare.titleVsShort;
					}
				}

				columnHeaders.push({
					index: columnHeaders.length,
					label: dataRoot.metricName,
					compareTitle,
					dsId: ds.numericId || 0,
				});
			}
		});

		// grouping rows by attribute
		this.tableOutputsByAttribute.map((outputsByAttribute, idx) => {
			const cells: TableStructureCell[] = [];
			outputsByAttribute.forEach((rowOutputs) => {
				const pointsForRow: { dsId: number; point: DChartPrimaryPoint }[] = [];
				rowOutputs.forEach((output) => {
					if (output.point) {
						const point = this.createDChartPointFromPoint(output, output.point);
						pointsForRow.push({
							dsId: output.dataRoot.dataSelect.numericId!,
							point,
						});
					}
				});
				pointsForRow.forEach(({ dsId, point }) => {
					// find matching header for each point
					const header = columnHeaders.find((item) => item.dsId === dsId);
					if (header) {
						const headerIndex = header.index,
							existingCell = cells[headerIndex];
						if (existingCell) {
							existingCell.points.push(point);
						} else {
							cells[headerIndex] = {
								headerIndex,
								points: [point],
								dsId,
								isAggregation: false,
							};
						}
					}
				});
			});
			if (cells.length) {
				const firstOutput = outputsByAttribute[0]?.[0];
				const dimensionValues = firstOutput?.dimensionValues;
				let mediaUrl: string | undefined;
				outputsByAttribute.some((output) => {
					mediaUrl = output?.[0]?.getDataRootMediaMapUrl();
					if (mediaUrl) {
						return true;
					}
				});
				rows.push({
					unsortedIndex: idx,
					label: {
						parts: buildDataLabelPartsFromDimensionsAndValuesMap(
							dimensionValues,
							firstOutput?.dataRoot.dimensions,
						),
						text: glueDimensionValues(dimensionValues),
					},
					mediaUrl,
					cells,
				});
			}
		});

		// One "Other" row in total (one cell per each metric column)
		if (this.allowOthersRow) {
			const cells: TableStructureCell[] = [];
			this.blockSetup.sortedDataSelectsShown.forEach((ds, idx) => {
				const root = ds.dataRoot;
				if (root) {
					const points: DChartPrimaryPoint[] = [];
					root.outputsOther.forEach((output) => {
						const pointTotal = output.pointTotal;
						if (pointTotal) {
							const point = this.createDChartPointFromPoint(
								output,
								output.pointTotal,
							);
							points.push(point);
						}
					});
					if (points.length) {
						cells[idx] = {
							headerIndex: idx,
							points,
							dsId: ds.numericId!,
							isAggregation: false,
						};
					}
				}
			});

			if (cells.length) {
				rows.push({
					unsortedIndex: rows.length,
					label: {
						parts: [{ type: DataLabelType.OTHER, value: 'Other' }],
						text: 'Other',
					},
					isOther: true,
					cells,
				});
			}
		}

		return {
			columnHeaders,
			rows,
		};
	}

	private getTableStructureForColumnsAuto(rowDim: string, colDim: string): TableStructure {
		const columnHeaders: TableStructureColumnHeader[] = [];
		const rows: TableStructureRow[] = [];

		// AUTO two-dim is only reached for a single shown metric (=DS) with exactly
		// two dims, so there is just one DS to read rows/columns from.
		const ds = this.blockSetup.sortedDataSelectsShown[0];
		const dataRoot = ds?.dataRoot;
		if (!ds || !dataRoot) {
			return { columnHeaders, rows };
		}
		const dsId = ds.numericId || 0;

		// columns: unique col-dim values (first-seen order)
		const columnVals: string[] = [];
		dataRoot.outputs.forEach((output) => {
			const colVal = output.dimensionValues[colDim] ?? '';
			if (!columnVals.includes(colVal)) {
				columnVals.push(colVal);
			}
		});
		columnVals.forEach((colVal, idx) => {
			columnHeaders.push({
				index: idx,
				label: colVal,
				compareTitle: '',
			});
		});

		// rows: one per rowDim value, in first-seen order
		const rowOrder: string[] = [];
		const outputsByRowVal = new Map<string, DsDataOutputTopLevel[]>();
		dataRoot.outputs.forEach((output) => {
			const rowVal = output.dimensionValues[rowDim];
			if (rowVal === undefined) {
				return;
			}
			let bucket = outputsByRowVal.get(rowVal);
			if (!bucket) {
				bucket = [];
				outputsByRowVal.set(rowVal, bucket);
				rowOrder.push(rowVal);
			}
			bucket.push(output);
		});

		rowOrder.forEach((rowVal) => {
			const cells: TableStructureCell[] = [];
			(outputsByRowVal.get(rowVal) || []).forEach((output) => {
				if (!output.point) {
					return;
				}
				const colVal = output.dimensionValues[colDim] ?? '';
				const colIdx = columnVals.indexOf(colVal);
				if (colIdx === -1) {
					return;
				}
				const point = this.createDChartPointFromPoint(output, output.point);
				const existingCell = cells[colIdx];
				if (existingCell) {
					existingCell.points.push(point);
				} else {
					cells[colIdx] = {
						headerIndex: colIdx,
						points: [point],
						dsId,
						isAggregation: false,
					};
				}
			});
			const dimensionPart: DataLabelPart = {
				type: DataLabelType.DIMENSION,
				value: rowVal,
				dimension: { key: rowDim, value: dataRoot.getDimensionLabel(rowDim) ?? '' },
			};
			rows.push({
				unsortedIndex: rows.length,
				label: { parts: [dimensionPart], text: rowVal },
				cells,
				dsId,
			});
		});

		// TODO: "Other" row (using outputsOther) goes here, once AUTO needs to honor
		// `allowOthersRow`.

		// TODO: aggregation row support — currently `aggregationData` is computed in
		// `getAggregationData()` based on showAggregationAsRow/Column. AUTO two-dim
		// is not yet wired into those paths.

		return { columnHeaders, rows };
	}

	private getTableStructureForColumnsGranulations(): TableStructure {
		const columnHeaders: TableStructureColumnHeader[] = [],
			rows: TableStructureRow[] = [];

		const dataRootForLabels = this.tableOutputsByMetricAttributeCombo[0]?.[0]?.dataRoot;
		const granulationLabels = dataRootForLabels?.granulationLabels || [];
		const invert = this.block.tableColumnInvert;
		const lastIdx = granulationLabels.length - 1;

		granulationLabels.forEach((labelData, idx) => {
			const headerIdx = invert ? lastIdx - idx : idx;
			columnHeaders[headerIdx] = {
				index: headerIdx,
				label: labelData.labelShort || labelData.label,
				compareTitle: '', // updated after
				ts: labelData.ts,
			};
		});

		let compareTitle = '';

		// just all metric and attribute combos
		this.tableOutputsByMetricAttributeCombo.forEach((outputsByMetricAndAttribute, idx) => {
			const cells: TableStructureCell[] = [];
			const ds = outputsByMetricAndAttribute[0]?.dataRoot.dataSelect,
				dsId = ds?.numericId || undefined;

			outputsByMetricAndAttribute.forEach((output) => {
				output.points.forEach((outputPoint) => {
					const pointIndex = outputPoint.index,
						cellIndex = invert ? lastIdx - pointIndex : pointIndex;
					const existingCell = cells[cellIndex],
						point = this.createDChartPointFromPoint(output, outputPoint);
					if (existingCell) {
						existingCell.points.push(point);
					} else {
						cells[cellIndex] = {
							headerIndex: cellIndex,
							points: [point],
							dsId,
							isAggregation: false,
						};
					}
				});
			});

			const firstOutput = outputsByMetricAndAttribute[0];
			rows.push({
				unsortedIndex: idx,
				label: {
					parts: firstOutput ? firstOutput.dataLabelParts : [],
					text: firstOutput?.shortTitleWithoutUnit || '',
				},
				mediaUrl: firstOutput ? firstOutput.getDataRootMediaMapUrl() : undefined,
				cells,
				dsId,
			});

			compareTitle = this.updateCompareColumnTitle(
				ds!.limitations.compareCMs[0]!,
				compareTitle,
			);
		});

		// One "Other" row per metric (=ds)
		if (this.allowOthersRow) {
			const dataSelects = this.blockSetup.sortedDataSelectsShown,
				isMultipleOthers =
					dataSelects.filter((ds) => ds.dataRoot?.outputsOther.length).length > 1;
			dataSelects.forEach((ds) => {
				const dsId = ds.numericId!,
					dataRoot = ds.dataRoot,
					otherOutputs = dataRoot?.outputsOther || []; // multiple means multiple units and/or timestamps
				if (dataRoot && otherOutputs.length) {
					const pointsByTs: Record<string, DChartPrimaryPoint[]> = {};
					otherOutputs.forEach((output) => {
						const pointTotal = output.pointTotal;
						if (pointTotal) {
							const point = this.createDChartPointFromPoint(
								output,
								output.pointTotal,
							);
							if (!pointsByTs[output.ts]) {
								pointsByTs[output.ts] = [];
							}
							pointsByTs[output.ts]!.push(point);
						}
					});

					const cells: TableStructureCell[] = [];
					Object.entries(pointsByTs).forEach(([ts, points]) => {
						const columnHeader = columnHeaders.find((c) => c.ts === +ts);
						if (columnHeader) {
							cells.push({
								headerIndex: columnHeader.index,
								points,
								dsId,
								isAggregation: false,
							});
						}
					});

					const titlePrefix = isMultipleOthers ? `${dataRoot.metricName} / ` : '';
					const otherParts: DataLabelPart[] = isMultipleOthers
						? [
								{ type: DataLabelType.METRIC, value: dataRoot.metricName },
								{ type: DataLabelType.OTHER, value: 'Other' },
							]
						: [{ type: DataLabelType.OTHER, value: 'Other' }];
					rows.push({
						unsortedIndex: rows.length,
						label: { parts: otherParts, text: `${titlePrefix}Other` },
						isOther: true,
						cells,
						dsId,
					});
				}
			});
		}

		columnHeaders.forEach((ch) => (ch.compareTitle = compareTitle));

		return {
			columnHeaders,
			rows,
		};
	}

	private updateCompareColumnTitle(compare: DsCompare, currentTitle: string) {
		// as soon as compares don't match, we set delta as title and don't check anymore
		if (currentTitle !== deltaSign) {
			if (compare && !compare.isNone) {
				if (currentTitle) {
					if (compare.titleVsShort !== currentTitle) {
						currentTitle = deltaSign;
					}
				} else {
					currentTitle = compare.titleVsShort;
				}
			}
		}
		return currentTitle;
	}

	private cleanupMediaEnabled(skipSave = false) {
		if (this.block.mediaEnabled && !this.showMediaSwitch) {
			this.block.mediaEnabled = false;
			if (!skipSave) {
				this.blockSetup.saveBlock();
			}
		}
	}

	// ------- general helpers --------

	get sortDataSelect() {
		// used for column: metrics case
		return this.blockSetup.dataSelectsShown.find((ds) => {
			return !ds.sortDataSelect;
		});
	}

	setSortDataSelectFrom(sortDataSelectId: number | null): void {
		this.blockSetup.dataSelects.forEach((ds) => {
			if (ds.numericId === sortDataSelectId) {
				ds.sortDataSelect = null;
			} else {
				ds.sortDataSelect = sortDataSelectId;
			}
			this.blockSetup.saveDataSelect(ds);
		});
	}

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// add sortDataSelect, if already set
		if (this.block.tableColumnType === TableColumnType.METRICS) {
			let sortDataSelectId = this.blockSetup.dataSelects.find(
				(item) => item.sortDataSelect,
			)?.sortDataSelect;
			if (!sortDataSelectId) {
				sortDataSelectId = this.blockSetup.firstShownDataSelect?.numericId || null;
			}
			dataSelect.sortDataSelect = sortDataSelectId;
		}

		// copy aggregation, if is block-level
		const dataSelectsShown = this.blockSetup.dataSelectsShown;
		if (this.showAggregationChoiceOnBlock && dataSelectsShown.length) {
			dataSelect.aggregation = dataSelectsShown[0]!.aggregation;
		}
	}

	override onDeleteDataSelect(dataSelect: DataSelect): void {
		super.onDeleteDataSelect(dataSelect);

		const id = dataSelect.numericId;
		if (id) {
			// change sortDataSelect that were pointing to this deleted DS
			const dataSelectsSortedByThis = this.blockSetup.dataSelects.filter(
				(item) => item.sortDataSelect === id,
			);
			if (dataSelectsSortedByThis.length) {
				this.setSortDataSelectFrom(this.blockSetup.firstShownDataSelect?.numericId || null);
				dataSelectsSortedByThis.forEach((ds) => {
					this.blockSetup.saveDataSelect(ds);
				});
			}
		}

		this.cleanupMediaEnabled();
	}

	override onChangeMetricInDataSelect(dataSelect: DataSelect): void {
		super.onChangeMetricInDataSelect(dataSelect);

		this.cleanupMediaEnabled();
	}

	changeSelectedColumnType(columnType: TableColumnType): void {
		this.block.tableColumnType = columnType;

		if (columnType === TableColumnType.GRANULATIONS) {
			this.blockSetup.updateIsNumberOnLimitations(false);
			this.setSortDataSelectFrom(null);
			this.blockSetup.removeSkipAutoUpdateOnPrimaryDataSelects();
		} else {
			this.blockSetup.updateIsNumberOnLimitations(true);
			if (columnType === TableColumnType.METRICS) {
				const sortDsId = this.blockSetup.firstShownDataSelect?.numericId;
				this.setSortDataSelectFrom(sortDsId || null);
			} else {
				this.setSortDataSelectFrom(null);
			}
		}

		if (!this.showBlockRecordLimitChoice) {
			// reset block recordLimit, if it had been changed
			this.block.recordLimit = this.recordLimit;
		}

		this.block.tableColumnInvert = false;

		void this.blockSetup.saveEverything();
	}

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// clear table column type, labelHeader and sortDataSelect
		this.loggerService.logToConsole(
			'[Vis change] from Table: clear table column type, labelHeader and sortDataSelect ref',
		);

		this.cleanupMediaEnabled(true);

		this.block.labelHeader = '';
		this.block.tableColumnType = null;
		this.block.tableColumnInvert = false;
		this.setSortDataSelectFrom(null);
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		this.tableStructure.rows.forEach((row) => {
			row.cells.forEach((cell) => {
				points.push(...cell.points);
			});
		});

		return points;
	}

	static override recordLimit = RecordLimit;
}
